import {
  Heading,
  useLargeFromMediaQuery,
  useLargeToMediaQuery,
  useMediumFromMediaQuery,
} from '@madpaws/design-system';
import classNames from 'classnames';
import getConfig from 'next/config';

import { ImageWithFallback } from '~/common/components/ImageWithFallback/ImageWithFallback';
import { DesktopSearchWidgetWrapper } from '~/common/components/SearchBarWidget/DesktopSearchWidgetWrapper/DesktopSearchWidgetWrapper';
import { MobileSearchWidgetWrapper } from '~/common/components/SearchBarWidget/MobileSearchWidgetWrapper/MobileSearchWidgetWrapper';

import styles from './Hero.module.css';
import { ProductReview } from './components/ProductReview';

import type { ReactElement } from 'react';
import type { SearchFilters } from '~/common/types/search';

const { publicRuntimeConfig } = getConfig();

type Props = {
  handleFiltersSave: (searchFilters: SearchFilters) => void;
  searchFilters: SearchFilters;
};

export const Hero = ({ searchFilters, handleFiltersSave }: Props): ReactElement => {
  const isLargeFromViewport = useLargeFromMediaQuery();
  const isMediumFromViewport = useMediumFromMediaQuery();
  const isLargeToMediaQuery = useLargeToMediaQuery();

  const mobileSearchWrapperClassNames = classNames({
    [styles.mobileSearchWrapper]: isLargeFromViewport,
  });

  return (
    <>
      <div className={styles.root}>
        {!isLargeFromViewport && (
          <div className={styles.mobileTitleWrapper}>
            <div className={styles.mobileTitleContainer}>
              <Heading
                fontFamily="heading"
                hasTightLineHeight
                level={2}
                size={isMediumFromViewport ? 'large1x' : 'medium'}
              >
                Trusted pet care, anytime, anywhere
              </Heading>
              <div>
                <Heading level={1}>Find trusted pet sitters & dog walkers near you</Heading>
              </div>
            </div>
            <div className={styles.mobileImageWrapper}>
              <ImageWithFallback
                alt="Picture of dog walker walking with two dogs"
                fallback={`${publicRuntimeConfig.staticPath}/images/homepage/mobile.png`}
                height={526}
                loading="eager"
                webpSrc={`${publicRuntimeConfig.staticPath}/images/homepage/mobile.webp`}
                width={1280}
              />
            </div>
          </div>
        )}

        {isLargeFromViewport && (
          <div className={styles.titleContainer}>
            <Heading
              alignment="centerAlign"
              fontFamily="heading"
              hasTightLineHeight
              level={2}
              size={isMediumFromViewport ? 'large1x' : 'medium'}
            >
              Trusted pet care, anytime, anywhere
            </Heading>
            <Heading alignment="centerAlign" level={1}>
              Find trusted pet sitters & dog walkers near you
            </Heading>
          </div>
        )}
        {!isLargeFromViewport && (
          <div className={mobileSearchWrapperClassNames}>
            <MobileSearchWidgetWrapper
              handleFiltersSave={handleFiltersSave}
              searchFilters={searchFilters}
            />
          </div>
        )}
        <DesktopSearchWidgetWrapper
          handleFiltersSave={handleFiltersSave}
          isExpandedSearchFiltersEnabled
          searchFilters={searchFilters}
        />
        {isLargeFromViewport && (
          <div className={styles.imageWrapper}>
            {isLargeToMediaQuery ? (
              <ImageWithFallback
                alt="Picture of dog walker walking with two dogs"
                fallback={`${publicRuntimeConfig.staticPath}/images/homepage/ExpandedHeroMedium.png`}
                height={526}
                loading="eager"
                webpSrc={`${publicRuntimeConfig.staticPath}/images/homepage/ExpandedHeroMedium.webp`}
                width={1280}
              />
            ) : (
              <ImageWithFallback
                alt="Picture of dog walker walking with two dogs"
                fallback={`${publicRuntimeConfig.staticPath}/images/homepage/ExpandedHeroLarge.png`}
                height={526}
                loading="eager"
                webpSrc={`${publicRuntimeConfig.staticPath}/images/homepage/ExpandedHeroLarge.webp`}
                width={1280}
              />
            )}
          </div>
        )}
      </div>
      {isLargeFromViewport && <ProductReview />}
    </>
  );
};
